import { ploomesDealPath } from '@v2/features/ploomes-deals/dealPath';
import type { SellerHistoryMonthItem } from '@v2/pages/commissioning/types';

export type HistoryExportLabels = {
    seller: string;
    sale: string;
    origin: string;
    type: string;
    deal: string;
    ploomesNumber: string;
    hubLink: string;
    ploomesLink: string;
    installment: string;
    clientDate: string;
    competence: string;
    role: string;
    saleSeller: string;
    saleManager: string;
    value: string;
    rate: string;
    commission: string;
    flags: string;
    status: string;
    originHub: string;
    originLegacy: string;
    typeStandard: string;
    typeSdr: string;
    statusNormal: string;
    statusDefaulted: string;
    statusForfeited: string;
    badgeRetroactive: string;
    entry: string;
    dealLevel: string;
};

export type HistoryExportRow = {
    recordId: number;
    isDefaulted: boolean;
    fillKey: 'defaulted' | 'sdr' | 'team' | 'sad' | 'buckler_one' | 'legacy' | 'hub' | 'normal';
    cells: string[];
    hubUrl: string;
    ploomesUrl: string;
    commissionAmount: number;
    ratePp: number | null;
    valueAmount: number | null;
};

const SALE_TYPE_KEYS = new Set(['sad', 'team', 'buckler_one']);

function saleNumbers(item: SellerHistoryMonthItem): string[] {
    if (item.sale_numbers && item.sale_numbers.length > 0) {
        return item.sale_numbers.map(String);
    }

    return [String(item.display_sale_number || item.internal_number || item.deal_number || item.deal_id)];
}

function formatDate(date: string | null | undefined): string {
    if (!date) return '—';
    const [y, m, d] = date.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('pt-BR');
}

function fmtRate(pp: number | null | undefined): string {
    if (pp == null || Number.isNaN(pp)) return '—';
    const rounded = Math.round(pp * 100) / 100;
    return `${String(rounded).replace('.', ',')}%`;
}

function brl(v: number): string {
    return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function copyRatePp(item: SellerHistoryMonthItem): number | null {
    const taShare = item.collaborator_share_pct ?? null;
    const isTaCollab =
        (item.commission_role === 'colaborador' || item.rate_basis === 'team_agreement_pool')
        && item.pool_rate_pp != null
        && taShare != null
        && taShare > 0
        && taShare < 100;

    return (isTaCollab ? item.pool_rate_pp : item.commission_rate_pp) ?? null;
}

function installmentLabel(item: SellerHistoryMonthItem, labels: HistoryExportLabels): string {
    if (item.installment_number == null) return labels.dealLevel;
    if (item.installment_number === 0) {
        return (item.entry_index ?? 0) > 0 ? `${labels.entry} ${item.entry_index}` : labels.entry;
    }
    return `${item.installment_number}/${item.total_installments || '—'}`;
}

function installmentRefMonth(item: SellerHistoryMonthItem): string | null {
    const raw = item.due_date ?? item.client_paid_at ?? null;
    if (!raw) return null;
    const d = new Date(`${raw.slice(0, 10)}T12:00:00`);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }).replace('.', '');
}

function clientPayDate(item: SellerHistoryMonthItem): string | null {
    return item.client_paid_at ?? item.due_date ?? item.finish_date ?? null;
}

function isSdrItem(item: SellerHistoryMonthItem): boolean {
    return item.commission_role === 'sdr' || item.rate_basis === 'sdr_owner_base';
}

function itemHasModeFlag(item: SellerHistoryMonthItem, key: string): boolean {
    if (key === 'legacy') {
        return Boolean(item.is_legacy_frozen)
            || (item.mode_flags ?? []).some((f) => f.key === 'legacy');
    }
    return (item.mode_flags ?? []).some((f) => f.key === key);
}

function saleTypeLabel(item: SellerHistoryMonthItem, labels: HistoryExportLabels): string {
    if (isSdrItem(item)) {
        return labels.typeSdr;
    }
    const flags = (item.mode_flags ?? []).filter((f) => SALE_TYPE_KEYS.has(f.key));
    if (flags.length > 0) {
        return flags.map((f) => f.label).join(', ');
    }
    return labels.typeStandard;
}

function extraFlags(item: SellerHistoryMonthItem, labels: HistoryExportLabels): string {
    const parts: string[] = [];
    for (const f of item.mode_flags ?? []) {
        if (f.key === 'legacy' || SALE_TYPE_KEYS.has(f.key) || f.key === 'sdr') {
            continue;
        }
        parts.push(f.label);
    }
    if (item.is_retroactive && !parts.some((p) => p === labels.badgeRetroactive)) {
        parts.push(labels.badgeRetroactive);
    }
    if (item.is_manual_inclusion) {
        parts.push('Manual');
    }
    return parts.join(', ');
}

function hubPath(item: SellerHistoryMonthItem): string {
    return ploomesDealPath({
        id: item.deal_id,
        internal_number: item.display_sale_number || item.internal_number,
    });
}

function absoluteUrl(pathOrUrl: string, appOrigin: string): string {
    if (!pathOrUrl) return '';
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
    const base = appOrigin.replace(/\/$/, '');
    const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
    return `${base}${path}`;
}

function fillKeyFor(item: SellerHistoryMonthItem, isDefaulted: boolean): HistoryExportRow['fillKey'] {
    if (isDefaulted || item.is_defaulted) return 'defaulted';
    if (isSdrItem(item)) return 'sdr';
    if (itemHasModeFlag(item, 'team')) return 'team';
    if (itemHasModeFlag(item, 'sad')) return 'sad';
    if (itemHasModeFlag(item, 'buckler_one')) return 'buckler_one';
    if (itemHasModeFlag(item, 'legacy')) return 'legacy';
    return 'hub';
}

function rowStatus(item: SellerHistoryMonthItem, isDefaulted: boolean, labels: HistoryExportLabels): string {
    if (item.bucket === 'forfeited' || (item as { is_forfeited?: boolean }).is_forfeited) {
        return labels.statusForfeited;
    }
    if (isDefaulted || item.is_defaulted) {
        return labels.statusDefaulted;
    }
    return labels.statusNormal;
}

export function historyExportHeaders(labels: HistoryExportLabels, partyColumns: string[] = []): string[] {
    const headers = [
        '#',
        labels.seller,
        labels.sale,
        labels.origin,
        labels.type,
        labels.deal,
        labels.ploomesNumber,
        labels.hubLink,
        labels.ploomesLink,
        labels.installment,
        labels.clientDate,
        labels.competence,
        labels.role,
    ];
    if (partyColumns.includes('seller')) {
        headers.push(labels.saleSeller);
    }
    if (partyColumns.includes('manager')) {
        headers.push(labels.saleManager);
    }
    headers.push(
        labels.value,
        labels.rate,
        labels.commission,
        labels.flags,
        labels.status,
    );

    return headers;
}

export function buildHistoryExportRows(
    items: SellerHistoryMonthItem[],
    options: {
        sellerName: string;
        labels: HistoryExportLabels;
        appOrigin: string;
        isDefaulted?: boolean;
        startIndex?: number;
        partyColumns?: string[];
    },
): HistoryExportRow[] {
    const isDefaulted = options.isDefaulted ?? false;
    const start = options.startIndex ?? 1;

    return items.map((item, idx) => {
        const hub = absoluteUrl(
            item.hub_url || hubPath(item),
            options.appOrigin,
        );
        const ploomes = item.ploomes_url
            ? absoluteUrl(item.ploomes_url, options.appOrigin)
            : '';
        const ratePp = copyRatePp(item);
        const refMonth = installmentRefMonth(item);
        const installment = refMonth
            ? `${installmentLabel(item, options.labels)} · ${refMonth}`
            : installmentLabel(item, options.labels);
        const parties = (item.role_parties ?? [])
            .map((p) => p.name)
            .filter(Boolean)
            .join(' · ');
        const role = parties
            ? `${item.role_label}${parties ? ` (${parties})` : ''}`
            : item.role_label;
        const partyColumns = options.partyColumns ?? [];
        const extraPartyCells: string[] = [];
        if (partyColumns.includes('seller')) {
            extraPartyCells.push(item.sale_seller?.name || '—');
        }
        if (partyColumns.includes('manager')) {
            extraPartyCells.push(item.sale_manager?.name || '—');
        }

        return {
            recordId: item.record_id,
            isDefaulted,
            fillKey: fillKeyFor(item, isDefaulted),
            hubUrl: hub,
            ploomesUrl: ploomes,
            commissionAmount: Number(item.final_amount ?? 0),
            ratePp,
            valueAmount: item.installment_amount ?? null,
            cells: [
                String(start + idx),
                options.sellerName,
                saleNumbers(item).join(' · '),
                itemHasModeFlag(item, 'legacy') ? options.labels.originLegacy : options.labels.originHub,
                saleTypeLabel(item, options.labels),
                item.deal_title || '—',
                item.deal_number ? String(item.deal_number) : '—',
                hub,
                ploomes || '—',
                installment,
                formatDate(clientPayDate(item)),
                formatDate(item.paid_at),
                role,
                ...extraPartyCells,
                item.installment_amount != null ? brl(item.installment_amount) : '—',
                fmtRate(ratePp),
                brl(Number(item.final_amount ?? 0)),
                extraFlags(item, options.labels),
                rowStatus(item, isDefaulted, options.labels),
            ],
        };
    });
}

/** TSV colável no Excel (tab + newline). Escapa aspas e tabs. */
export function toTsv(headers: string[], rows: HistoryExportRow[]): string {
    const escape = (value: string) => {
        const v = value.replace(/\r?\n/g, ' ').replace(/\t/g, ' ');
        if (/["]/.test(v)) {
            return `"${v.replace(/"/g, '""')}"`;
        }
        return v;
    };

    const lines = [
        headers.map(escape).join('\t'),
        ...rows.map((row) => row.cells.map(escape).join('\t')),
    ];

    return lines.join('\n');
}
